import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { selectIsLogoutProcessing } from '@app/app.reducer';
import { FormFactory } from '@core/data-access/models/form/form-mediator.model';
import { FormName } from '@core/data-access/models/form/form.model';
import { DynamicFieldOptionsTranslationFactory } from '@core/data-access/services/dynamic-form-field/dynamic-field-options-translation-factory.service';
import { DynamicFormFactory } from '@core/data-access/services/dynamic-form-field/dynamic-form-factory.service';
import { selectBuyerPerson } from '@core/features/buyer-information/store/buyer-information.selectors';
import {
  TicketHolderForms,
  TicketHolderQuestionnaireForms
} from '@core/features/ticket-assignment/models/ticket-assignment-store.model';
import { TicketHolderQuestionnaireFormStates } from '@core/features/ticket-assignment/models/ticket-holder-questionnaire-fom.model';
import {
  selectTicketHolderQuestionnaireDynamicFieldsByTicketPersonId,
  selectTicketHolderQuestionnaireForms,
  selectTicketHolderQuestionnaireFormsTariffUuidsByPersonUuid
} from '@core/features/ticket-assignment/store/ticket-assignment.selectors';
import {
  reduceTicketHolderQuestionnaireFormStatePipe,
  updateFormFieldsFilterPipe
} from '@core/features/ticket-assignment/utils/ticket-holder-forms-factory-helpers';
import { questionnaireDynamicFormFieldMapper } from '@core/utils/dynamic-form-field/dynamic-form-field-mappers';
import { select, Store } from '@ngrx/store';
import { TariffUuid, TariffUuids, TicketPersonId } from '@products/models/tariff.model';
import { selectTicketPersonIdByBookedTariffUuid } from '@store/products/booking/tariff/booking-tariff.selectors';
import { filter as _filter, reduce as _reduce, difference, includes, isEmpty, isEqual, isNil, keys } from 'lodash';
import {
  BehaviorSubject,
  combineLatest,
  concatMap,
  filter,
  first,
  from,
  iif,
  map,
  merge,
  Observable,
  of,
  pairwise,
  pipe,
  startWith,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TicketHolderQuestionnaireFormsFactory implements FormFactory {
  readonly #destroyRef = inject(DestroyRef);
  readonly #store = inject(Store);
  readonly #dynamicFormFactory = inject(DynamicFormFactory);
  readonly #dynamicFieldOptionsTranslationFactory = inject(DynamicFieldOptionsTranslationFactory);

  readonly #initialState = new Map();

  readonly #resetFormState$ = new BehaviorSubject(false);

  readonly #ticketHolderQuestionnaireFormsState$ = this.#store.select(selectTicketHolderQuestionnaireForms);

  readonly #updateTicketHolderQuestionnaireFormStatePairwiseFilter$ =
    this.updateTicketHolderQuestionnaireFormStatePairwiseFilter$();

  readonly #isFormStateCreated = signal(false);

  readonly state = toSignal(this.state$(), { initialValue: this.#initialState });

  resetFormState() {
    this.#resetFormState$.next(true);
  }

  private state$(): Observable<TicketHolderQuestionnaireFormStates> {
    return merge(this.setFormState$(), this.updateFormState$(), this.resetFormsState$()).pipe(
      takeUntilDestroyed(this.#destroyRef)
    );
  }

  private setFormState$(): Observable<TicketHolderQuestionnaireFormStates> {
    return this.#ticketHolderQuestionnaireFormsState$.pipe(
      startWith({}),
      pairwise(),
      filter(
        ([previousTicketHolderQuestionnaireForms, currentTicketHolderQuestionnaireForms]) =>
          !isEqual(keys(previousTicketHolderQuestionnaireForms), keys(currentTicketHolderQuestionnaireForms))
      ),
      map(([previousTicketHolderQuestionnaireForms, currentTicketHolderQuestionnaireForms]) => {
        const previousTariffUuids = keys(previousTicketHolderQuestionnaireForms);
        const currentTariffUuids = keys(currentTicketHolderQuestionnaireForms);

        const createOrAddFormToFormState = previousTariffUuids.length < currentTariffUuids.length;
        const tariffUuidDifference = createOrAddFormToFormState
          ? difference(currentTariffUuids, previousTariffUuids)
          : difference(previousTariffUuids, currentTariffUuids);

        return {
          ticketHolderQuestionnaireForms: currentTicketHolderQuestionnaireForms,
          createOrAddFormToFormState,
          tariffUuidDifference
        };
      }),
      switchMap(({ createOrAddFormToFormState, ticketHolderQuestionnaireForms, tariffUuidDifference }) =>
        iif(
          () => createOrAddFormToFormState,
          this.createOrAddFormToFormState$(ticketHolderQuestionnaireForms, tariffUuidDifference),
          this.removeFormFromFormStates$(tariffUuidDifference)
        )
      )
    );
  }

  private createOrAddFormToFormState$(
    ticketHolderQuestionnaireForms: TicketHolderQuestionnaireForms,
    createOrAddFormTariffUuids: TariffUuids
  ): Observable<TicketHolderQuestionnaireFormStates> {
    return iif(
      () => !this.#isFormStateCreated(),
      this.createFormStates$(ticketHolderQuestionnaireForms, createOrAddFormTariffUuids),
      this.addFormStateToFormStates$(ticketHolderQuestionnaireForms, createOrAddFormTariffUuids)
    );
  }

  private createFormStates$(
    ticketHolderQuestionnaireForms: TicketHolderQuestionnaireForms,
    createFormTariffUuids: TariffUuids
  ): Observable<TicketHolderQuestionnaireFormStates> {
    return from(createFormTariffUuids).pipe(
      this.createFormSatePipe(ticketHolderQuestionnaireForms),
      tap(() => this.#isFormStateCreated.set(true))
    );
  }

  private addFormStateToFormStates$(
    ticketHolderQuestionnaireForms: TicketHolderQuestionnaireForms,
    addFormTariffUuids: TariffUuids
  ): Observable<TicketHolderQuestionnaireFormStates> {
    return from(addFormTariffUuids).pipe(
      this.createFormSatePipe(ticketHolderQuestionnaireForms),
      map((updatedFormStates) => new Map([...this.state(), ...updatedFormStates]))
    );
  }

  private createFormSatePipe(ticketHolderQuestionnaireForms: TicketHolderQuestionnaireForms) {
    return pipe(
      map((tariffUuid: TariffUuid) => ({
        tariffUuid,
        person: ticketHolderQuestionnaireForms[tariffUuid]?.person || null,
        formFields: ticketHolderQuestionnaireForms[tariffUuid]?.formFields || {}
      })),
      concatMap(({ tariffUuid, person, formFields }) =>
        this.#store.pipe(
          select(selectTicketPersonIdByBookedTariffUuid(tariffUuid)),
          first(),
          switchMap((ticketPersonId: TicketPersonId) =>
            this.#store.pipe(
              select(selectTicketHolderQuestionnaireDynamicFieldsByTicketPersonId(ticketPersonId)),
              first(),
              map((ticketHolderQuestionnaireDynamicFields) => {
                const dynamicFormFields = questionnaireDynamicFormFieldMapper(ticketHolderQuestionnaireDynamicFields);

                return {
                  viewModel: {},
                  dynamicFormFields,
                  form: {
                    formName: FormName.TicketHolderQuestionnaire,
                    formGroup: this.#dynamicFormFactory.createForm(dynamicFormFields, formFields)
                  },
                  person,
                  tariffUuid
                };
              }),
              tap(({ dynamicFormFields }) => {
                this.#dynamicFieldOptionsTranslationFactory.createTranslatedDynamicFieldOptions(
                  FormName.TicketHolderQuestionnaire,
                  dynamicFormFields
                );
              })
            )
          )
        )
      ),
      reduceTicketHolderQuestionnaireFormStatePipe()
    );
  }

  private removeFormFromFormStates$(
    removeFormTariffUuids: TariffUuids
  ): Observable<TicketHolderQuestionnaireFormStates> {
    return of(removeFormTariffUuids).pipe(
      filter(() => this.#isFormStateCreated()),
      map(
        (removeFormTariffUuids) => new Map(_filter([...this.state()], ([key]) => !includes(removeFormTariffUuids, key)))
      )
    );
  }

  private updateFormState$(): Observable<TicketHolderQuestionnaireFormStates> {
    return toObservable(this.#isFormStateCreated).pipe(
      filter((isFormStateCreated) => isFormStateCreated),
      switchMap(() =>
        merge(
          this.recreateFormOnTicketHolderChange$(),
          this.updateFormStateOnSetTicketHolderQuestionnaireForms$(),
          this.updateFormStateOnBuyerPersonChange$()
        )
      )
    );
  }

  private recreateFormOnTicketHolderChange$(): Observable<TicketHolderQuestionnaireFormStates> {
    return this.#updateTicketHolderQuestionnaireFormStatePairwiseFilter$.pipe(
      map(([previousTicketHolderFormChanges, currentTicketHolderFormChanges]) =>
        _reduce(
          keys(currentTicketHolderFormChanges),
          (ticketHolderForms: TicketHolderForms, tariffUuid) => {
            const previousPersonChange = previousTicketHolderFormChanges[tariffUuid]?.person;
            const currentPersonChange = currentTicketHolderFormChanges[tariffUuid]?.person;

            if (isEqual(previousPersonChange, currentPersonChange)) {
              return ticketHolderForms;
            }

            ticketHolderForms[tariffUuid] = currentTicketHolderFormChanges[tariffUuid];

            return ticketHolderForms;
          },
          {}
        )
      ),
      filter((ticketHolderForms) => !isEmpty(ticketHolderForms)),
      switchMap((ticketHolderForms) => from(keys(ticketHolderForms)).pipe(this.createFormSatePipe(ticketHolderForms))),
      map((updatedFormStates) => new Map([...this.state(), ...updatedFormStates]))
    );
  }

  private updateFormStateOnSetTicketHolderQuestionnaireForms$(): Observable<TicketHolderQuestionnaireFormStates> {
    return this.#updateTicketHolderQuestionnaireFormStatePairwiseFilter$.pipe(
      switchMap(([previousTicketHolderFormChanges, currentTicketHolderFormChanges]) =>
        from(keys(currentTicketHolderFormChanges)).pipe(
          updateFormFieldsFilterPipe(
            previousTicketHolderFormChanges,
            currentTicketHolderFormChanges,
            ({ changedFormFields }) => !isEmpty(changedFormFields)
          ),
          map(({ tariffUuid, changedFormFields }) => {
            const { viewModel, dynamicFormFields, form, person } = this.state().get(tariffUuid);

            return {
              viewModel,
              dynamicFormFields: this.#dynamicFormFactory.updateForm(
                dynamicFormFields,
                changedFormFields,
                form.formGroup
              ),
              person,
              form,
              tariffUuid
            };
          }),
          reduceTicketHolderQuestionnaireFormStatePipe()
        )
      ),
      map((updatedFormStates) => new Map([...this.state(), ...updatedFormStates]))
    );
  }

  private updateFormStateOnBuyerPersonChange$() {
    return combineLatest({
      resetFormState: this.#resetFormState$,
      person: this.#store.select(selectBuyerPerson)
    }).pipe(
      filter(({ resetFormState, person }) => this.#isFormStateCreated() && !resetFormState && !isNil(person)),
      switchMap(({ person }) =>
        this.#store.pipe(
          select(selectTicketHolderQuestionnaireFormsTariffUuidsByPersonUuid(person.uuid)),
          first(),
          map((ticketHolderTariffUuids) =>
            _reduce(
              ticketHolderTariffUuids,
              (updatedFormStates, tariffUuid) => {
                const { viewModel, dynamicFormFields, form } = this.state().get(tariffUuid);

                updatedFormStates.set(tariffUuid, { viewModel, dynamicFormFields, form, person });

                return updatedFormStates;
              },
              new Map()
            )
          )
        )
      ),
      map((updatedFormStates) => new Map([...this.state(), ...updatedFormStates]))
    );
  }

  private updateTicketHolderQuestionnaireFormStatePairwiseFilter$() {
    return this.#ticketHolderQuestionnaireFormsState$.pipe(
      pairwise(),
      filter(([previousTicketHolderFormChanges, currentTicketHolderFormChanges]) => {
        const previousTicketHolderFormChangesKeys = keys(previousTicketHolderFormChanges);
        const currentTicketHolderFormChangesKeys = keys(currentTicketHolderFormChanges);

        return (
          !isEmpty(previousTicketHolderFormChangesKeys) &&
          !isEmpty(currentTicketHolderFormChangesKeys) &&
          isEqual(previousTicketHolderFormChangesKeys, currentTicketHolderFormChangesKeys)
        );
      })
    );
  }

  private resetFormsState$(): Observable<TicketHolderQuestionnaireFormStates> {
    return this.#resetFormState$.pipe(
      withLatestFrom(this.#store.select(selectIsLogoutProcessing)),
      filter(([resetFormState, isLogoutProcessing]) => resetFormState && !isLogoutProcessing),
      map(() => this.#initialState),
      tap(() => {
        this.#isFormStateCreated.set(false);

        this.#resetFormState$.next(false);
      })
    );
  }
}
